import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, VStack, HStack, Pressable, Icon, Heading,
} from '@gluestack-ui/themed';
import {
  PanelRightClose, PanelRight,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Task, TaskScope } from '@/student/types';
import { AIPresenceOrb } from '@/common/components/presence/AIPresenceOrb';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import { useTaskSubmissionState } from '../stores/TaskSubmissionStore';
import AiTutorPresenceBand from './aiTutor/AiTutorPresenceBand';
import EvaluationCard from './aiTutor/EvaluationCard';
import {
  deriveCompactOrbState,
  PRESENCE_BAND_ACTIVE_HEIGHT,
  PRESENCE_BAND_EXIT_MS,
} from './aiTutor/aiTutorPresenceState';

type Props = {
  task: Task;
  taskScope: TaskScope;
  readonly?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Hide the thumbs-up/down feedback controls (used by preview). */
  hideStudentFeedbackControls?: boolean;
  /** Called when student submits for evaluation. Omit in preview mode. */
  submitAction?: () => void;
  /** Called to cancel an in-flight evaluation. Omit in preview mode. */
  cancelEvaluationAction?: () => void;
  /** Called to retry after a stream failure. */
  retryEvaluationAction?: () => void;
};

const COMPACT_ORB_SIZE = 24;

type RetainedBandProps = {
  progress: ReturnType<typeof useTaskSubmissionState>['evaluationProgress'];
  errorMessage: string | null;
  formativeFeedback: ReturnType<typeof useTaskSubmissionState>['formativeFeedback'];
  formativeFeedbackVersion: number;
  formativeFeedbackRunId: ReturnType<typeof useTaskSubmissionState>['formativeFeedbackRunId'];
  activityPulse: number;
};

const AssistanceDrawer: React.FC<Props> = ({
  task,
  taskScope,
  readonly,
  isCollapsed = false,
  onToggleCollapse,
  hideStudentFeedbackControls = false,
  submitAction,
  cancelEvaluationAction,
  retryEvaluationAction,
}) => {
  const isMarkedAsDone = task?.submission?.state === 'marked_as_done';
  const { t } = useTranslation();
  const reducedMotion = useOrbReducedMotion();
  const {
    isEvaluating,
    evaluationProgress,
    evaluationError,
    formativeFeedbackPartial,
    formativeFeedback,
    formativeFeedbackVersion,
    formativeFeedbackRunId,
  } = useTaskSubmissionState();

  const hasPersistentResponse = Boolean(formativeFeedback) && !isEvaluating && !evaluationError;
  const showPresenceBand = !isCollapsed && (isEvaluating || !!evaluationError || hasPersistentResponse);

  const compactOrbState = useMemo(
    () => deriveCompactOrbState({
      isEvaluating,
      progress: evaluationProgress,
      errorMessage: evaluationError,
      hasCompleteFeedback: hasPersistentResponse,
    }),
    [isEvaluating, evaluationProgress, evaluationError, hasPersistentResponse],
  );

  const prevFeedbackPartialRef = useRef(formativeFeedbackPartial);
  const prevFormativeVersionRef = useRef(formativeFeedbackVersion);
  const prevIsEvaluatingForPulseRef = useRef(isEvaluating);
  const previousTaskIdRef = useRef(task.id);
  const [activityPulse, setActivityPulse] = useState(0);
  const bandExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const [isBandMounted, setIsBandMounted] = useState(showPresenceBand);
  const [retainedBandProps, setRetainedBandProps] = useState<RetainedBandProps>({
    progress: evaluationProgress,
    errorMessage: evaluationError,
    formativeFeedback,
    formativeFeedbackVersion,
    formativeFeedbackRunId,
    activityPulse: 0,
  });

  useEffect(() => {
    if (
      isEvaluating
      && formativeFeedbackPartial
      && formativeFeedbackPartial !== prevFeedbackPartialRef.current
    ) {
      setActivityPulse((count) => count + 1);
    }
    prevFeedbackPartialRef.current = formativeFeedbackPartial;
  }, [formativeFeedbackPartial, isEvaluating]);

  useEffect(() => {
    const versionAdvanced = formativeFeedbackVersion > prevFormativeVersionRef.current;
    const completedEvaluation = prevIsEvaluatingForPulseRef.current && !isEvaluating && !!formativeFeedback;
    if (completedEvaluation && versionAdvanced) {
      setActivityPulse((count) => count + 1);
    }
    prevFormativeVersionRef.current = formativeFeedbackVersion;
    prevIsEvaluatingForPulseRef.current = isEvaluating;
  }, [formativeFeedbackVersion, isEvaluating, formativeFeedback]);

  useEffect(() => {
    if (bandExitTimerRef.current) {
      clearTimeout(bandExitTimerRef.current);
      bandExitTimerRef.current = null;
    }

    if (showPresenceBand) {
      if (isMountedRef.current) {
        setIsBandMounted(true);
        setRetainedBandProps({
          progress: evaluationProgress,
          errorMessage: evaluationError,
          formativeFeedback,
          formativeFeedbackVersion,
          formativeFeedbackRunId,
          activityPulse,
        });
      }
      return undefined;
    }

    if (isBandMounted) {
      if (reducedMotion) {
        if (isMountedRef.current) {
          setIsBandMounted(false);
        }
        return undefined;
      }
      bandExitTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) {
          bandExitTimerRef.current = null;
          return;
        }
        setIsBandMounted(false);
        bandExitTimerRef.current = null;
      }, PRESENCE_BAND_EXIT_MS);
    }

    return () => {
      if (bandExitTimerRef.current) {
        clearTimeout(bandExitTimerRef.current);
        bandExitTimerRef.current = null;
      }
    };
  }, [
    activityPulse,
    evaluationError,
    evaluationProgress,
    formativeFeedback,
    formativeFeedbackVersion,
    formativeFeedbackRunId,
    isBandMounted,
    reducedMotion,
    showPresenceBand,
  ]);

  useEffect(() => {
    if (previousTaskIdRef.current === task.id) return;
    previousTaskIdRef.current = task.id;
    if (bandExitTimerRef.current) {
      clearTimeout(bandExitTimerRef.current);
      bandExitTimerRef.current = null;
    }
    if (!isMountedRef.current) return;
    setIsBandMounted(showPresenceBand);
    if (showPresenceBand) {
      setRetainedBandProps({
        progress: evaluationProgress,
        errorMessage: evaluationError,
        formativeFeedback,
        formativeFeedbackVersion,
        formativeFeedbackRunId,
        activityPulse,
      });
    }
  }, [
    activityPulse,
    evaluationError,
    evaluationProgress,
    formativeFeedback,
    formativeFeedbackVersion,
    formativeFeedbackRunId,
    showPresenceBand,
    task.id,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (bandExitTimerRef.current) {
        clearTimeout(bandExitTimerRef.current);
        bandExitTimerRef.current = null;
      }
    };
  }, []);

  const bandTransitionStyle = reducedMotion
    ? undefined
    : {
      transitionProperty: 'height, opacity',
      transitionDuration: `${PRESENCE_BAND_EXIT_MS}ms`,
      transitionTimingFunction: 'ease-out',
    };
  const bandSlotStyle = useMemo(() => {
    if (!showPresenceBand) {
      return {
        height: 0,
        maxHeight: PRESENCE_BAND_ACTIVE_HEIGHT,
        opacity: 0,
      };
    }
    if (hasPersistentResponse) {
      return {
        minHeight: PRESENCE_BAND_ACTIVE_HEIGHT,
        opacity: 1,
      };
    }
    return {
      height: PRESENCE_BAND_ACTIVE_HEIGHT,
      maxHeight: PRESENCE_BAND_ACTIVE_HEIGHT,
      opacity: 1,
    };
  }, [showPresenceBand, hasPersistentResponse]);

  // Single-presence invariant: header miniature only when expanded and the band is fully unmounted.
  const showHeaderOrb = !isCollapsed && !isBandMounted;

  return (
    <VStack position="relative" flex={1} testID="assistance-drawer">
      {/* Header - Height matches left sidebar header (72px) for visual rhythm */}
      <HStack
        h={isCollapsed ? '100%' : 72}
        justifyContent="space-between"
        alignItems={isCollapsed ? 'flex-start' : 'center'}
        px="$6"
        borderBottomWidth={isCollapsed ? 0 : 1}
        borderColor="$borderLight"
      >
        {isCollapsed ? (
          <VStack flex={1} py="$4" alignItems="center" space="md">
            <Pressable
              onPress={onToggleCollapse}
              hitSlop={8}
              p="$2"
              borderRadius="$md"
              sx={{ ':hover': { bg: '$bgHover' } }}
              testID="ai-tutor-expand-control"
              accessibilityRole="button"
              accessibilityLabel={t('student.task.aiTutor.expandPane', 'Expand AI Tutor pane')}
            >
              <Icon as={PanelRight} color="$textSubtle" size="lg" />
            </Pressable>
            <Box testID="ai-tutor-rail-orb-wrapper">
              <AIPresenceOrb
                state={compactOrbState}
                size={COMPACT_ORB_SIZE}
                activityPulse={activityPulse}
                testID="ai-tutor-rail-orb"
              />
            </Box>
          </VStack>
        ) : (
          <>
            <HStack space="sm" alignItems="center">
              {showHeaderOrb ? (
                <Box testID="ai-tutor-header-orb-wrapper">
                  <AIPresenceOrb
                    state={compactOrbState}
                    size={COMPACT_ORB_SIZE}
                    activityPulse={activityPulse}
                    testID="ai-tutor-header-orb"
                  />
                </Box>
              ) : null}
              <Heading size="sm" color="$textHeading">
                {t('navigation.taskscreen.aiTutor')}
              </Heading>
            </HStack>

            <HStack space="sm" alignItems="center">
              <Pressable
                onPress={onToggleCollapse}
                hitSlop={8}
                p="$2"
                borderRadius="$md"
                sx={{ ':hover': { bg: '$bgHover' } }}
                testID="ai-tutor-collapse-control"
                accessibilityRole="button"
                accessibilityLabel={t('student.task.aiTutor.collapsePane', 'Collapse AI Tutor pane')}
              >
                <Icon as={PanelRightClose} color="$textSubtle" size="lg" />
              </Pressable>
            </HStack>
          </>
        )}
      </HStack>

      <Box
        overflow={hasPersistentResponse ? 'visible' : 'hidden'}
        style={bandSlotStyle}
        pointerEvents={showPresenceBand ? 'auto' : 'none'}
        aria-hidden={!showPresenceBand}
        accessibilityElementsHidden={!showPresenceBand}
        importantForAccessibility={showPresenceBand ? 'auto' : 'no-hide-descendants'}
        sx={bandTransitionStyle}
        testID="ai-tutor-presence-band-slot"
      >
        {isBandMounted && (
          <AiTutorPresenceBand
            progress={evaluationProgress ?? retainedBandProps.progress}
            errorMessage={retainedBandProps.errorMessage}
            formativeFeedback={retainedBandProps.formativeFeedback}
            formativeFeedbackVersion={retainedBandProps.formativeFeedbackVersion}
            formativeFeedbackRunId={retainedBandProps.formativeFeedbackRunId}
            initialStudentFeedback={task?.submission?.evaluationStudentFeedback}
            courseId={taskScope.courseId}
            moduleId={taskScope.moduleId}
            taskId={taskScope.taskId}
            readonly={readonly}
            hideStudentFeedbackControls={hideStudentFeedbackControls}
            activityPulse={retainedBandProps.activityPulse}
            onCancel={cancelEvaluationAction}
            onRetry={retryEvaluationAction}
          />
        )}
      </Box>

      {/* Evaluation section - hidden when collapsed */}
      {!isCollapsed && (
        <VStack flex={1}>
          <Box p="$6" pb="$4">
            <EvaluationCard
              onSubmit={submitAction}
              onCancelEvaluation={cancelEvaluationAction}
              readonly={readonly}
              isMarkedAsDone={isMarkedAsDone}
              gated={task?.evaluation?.completionPolicyEffective === 'gated'}
            />
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

export default AssistanceDrawer;
